// Advent chimney: a brick chimney on a fireplace, stacked from one ring per day. Each ring
// is two brick courses with a slot for its numbered shelf; every second ring is turned by
// 180° (slot on the other side: odd days left, even right) — the bricks repeat, so all rings
// print from one file and each day is two pieces: ring + shelf. Day 1 is the
// lowest: pull its shelf, the treats drop through the empty shaft onto the hearth ramp and
// roll out of the fireplace. Filling goes bottom-up: shelf in, treats in from the top, next
// shelf … Day 24 has extra room above it (plain rings). Santa (head first) is the lid.
//
// Coordinates: mm, x = width, y = depth (front −, back +), z = up; tower centred on x = y = 0.
// Everything is modular so it fits the Snapmaker U1 (270 mm) and prints in four colours.

import { textSection } from './text.js'

export const CHIMNEY_PARAMS = [
  { group: 'Kamin', key: 'innerX', label: 'Fach innen Breite', default: 120, min: 50, max: 180 },
  { group: 'Kamin', key: 'innerY', label: 'Fach innen Tiefe', default: 80, min: 40, max: 140 },
  { group: 'Kamin', key: 'room', label: 'Platz pro Tag (Höhe)', default: 30, min: 10, max: 60, step: 0.5 },
  { group: 'Kamin', key: 'days', label: 'Tage', unit: 'Stk', default: 24, min: 6, max: 30 },
  { group: 'Kamin', key: 'extraLast', label: 'Extra-Höhe letzter Tag', default: 20, min: 0, max: 100 },
  { group: 'Kamin', key: 'wall', label: 'Wandstärke', default: 2.8, min: 2.4, max: 4, step: 0.2 },
  { group: 'Fächer', key: 'shelfT', label: 'Fach-Dicke', default: 2, min: 1.4, max: 3, step: 0.1 },
  { group: 'Fächer', key: 'tab', label: 'Lasche außen', default: 22, min: 14, max: 35 },
  { group: 'Fächer', key: 'numH', label: 'Zahlenhöhe', default: 11, min: 6, max: 16, step: 0.5 },
  { group: 'Fächer', key: 'fit', label: 'Spiel', default: 0.2, min: 0.1, max: 0.5, step: 0.05 },
]

export const chimneyDefaults = () => Object.fromEntries(CHIMNEY_PARAMS.map((p) => [p.key, p.default]))

const JOINT = 4 // plug-in joint between modules (spigot up, socket down)

export function buildChimney(M, raw = {}) {
  const P = { ...chimneyDefaults(), ...raw }
  const { Manifold, CrossSection } = M
  const { innerX: Ix, innerY: Iy, wall, shelfT, fit } = P
  const days = Math.round(P.days)
  const box = (x0, y0, z0, x1, y1, z1) => Manifold.cube([x1 - x0, y1 - y0, z1 - z0]).translate([x0, y0, z0])
  const cbox = (w, d, z0, z1) => box(-w / 2, -d / 2, z0, w / 2, d / 2, z1)
  const rbox = (w, d, z0, z1, r) => Manifold.extrude(CrossSection.square([w - 2 * r, d - 2 * r], true).offset(r, 'Round', 2, 32), z1 - z0).translate([0, 0, z0])
  const Ox = Ix + 2 * wall, Oy = Iy + 2 * wall // outer chimney
  const slotH = shelfT + 2 * fit
  const groove = Math.min(1.2, wall / 2)
  const pitch = P.room + slotH // a ring: the room above its shelf plus the shelf's slot

  // Spigot on top of a module, socket under the next one: the shaft stays Ix × Iy throughout.
  const spigot = (z) => cbox(Ix + 2.4, Iy + 2.4, z, z + JOINT).subtract(cbox(Ix, Iy, z - 1, z + JOINT + 1))
  const socket = () => cbox(Ix + 2.4 + 2 * fit, Iy + 2.4 + 2 * fit, -1, JOINT + 0.2)

  // ── chimney ring (one per day) ─────────────────────────────────────────
  // Two brick courses of pitch/2; the seam to the next ring is a mortar line (a 0.5 mm
  // rebate on both rings), the courses run in bond with joints symmetric on every face — so a
  // ring turned by 180° looks the same. The shelf slot sits in the middle mortar line.
  const h = pitch
  const slotZ = h / 2 - slotH / 2
  const ringOf = (withSlot) => {
    let r = cbox(Ox, Oy, 0, h).subtract(cbox(Ix, Iy, -1, h + 1)).subtract(socket()).add(spigot(h))
    const ringCut = (z0, z1, d) => cbox(Ox + 2, Oy + 2, z0, z1).subtract(cbox(Ox - 2 * d, Oy - 2 * d, z0 - 1, z1 + 1))
    const cuts = [ringCut(-1, 0.5, 0.5), ringCut(h - 0.5, h + 0.01, 0.5), ringCut(h / 2 - 0.5, h / 2 + 0.5, 0.6)]
    // Head joints: lower course at ±¼ of each face, upper course in the middle (running bond).
    const faces = [
      (u, z0, z1) => box(u - 0.5, -Oy / 2 - 1, z0, u + 0.5, -Oy / 2 + 0.6, z1),
      (u, z0, z1) => box(u - 0.5, Oy / 2 - 0.6, z0, u + 0.5, Oy / 2 + 1, z1),
      (u, z0, z1) => box(-Ox / 2 - 1, u - 0.5, z0, -Ox / 2 + 0.6, u + 0.5, z1),
      (u, z0, z1) => box(Ox / 2 - 0.6, u - 0.5, z0, Ox / 2 + 1, u + 0.5, z1),
    ]
    faces.forEach((joint, f) => {
      const W = f < 2 ? Ox : Oy
      for (const u of [-W / 4, W / 4]) cuts.push(joint(u, 0.2, h / 2 - 0.2))
      cuts.push(joint(0, h / 2 + 0.2, h - 0.2))
    })
    r = r.subtract(Manifold.union(cuts))
    if (!withSlot) return r
    // (Slot and groove reach 0.3 mm into the side walls: no face in the plane of the inside.)
    // Slot through the left wall, groove in the right one.
    return r.subtract(box(-Ox / 2 - 1, -Iy / 2 - 0.3, slotZ, -Ix / 2 + 0.01, Iy / 2 + 0.3, slotZ + slotH))
      .subtract(box(Ix / 2 - 0.01, -Iy / 2 - 0.3, slotZ, Ix / 2 + groove, Iy / 2 + 0.3, slotZ + slotH))
  }
  const ring = ringOf(true), plain = ringOf(false)
  // Odd days enter from the left, even days from the right (that ring turned 180°).
  const sideOf = (day) => (day % 2 ? -1 : 1)
  const ringFor = (day) => (sideOf(day) < 0 ? ring : ring.rotate([0, 0, 180]))

  // ── shelf cards ────────────────────────────────────────────────────────
  // Flat card across the shaft, its far end in the groove, a tab out of the side wall.
  const cardW = Iy - 2 * fit - 0.2, tabW = Math.min(36, Iy * 0.45)
  const shelf = (day) => {
    const sx = sideOf(day)
    const far = Ix / 2 + groove - fit
    const body = box(Math.min(-sx * far, sx * Ox / 2), -cardW / 2, 0, Math.max(-sx * far, sx * Ox / 2), cardW / 2, shelfT)
    const tabOut = Manifold.extrude(CrossSection.square([P.tab + 4, tabW], true).offset(-4, 'Round').offset(4, 'Round', 2, 24), shelfT).translate([sx * (Ox / 2 + P.tab / 2 - 2), 0, 0])
    const card = body.add(tabOut)
    const num = Manifold.extrude(textSection(CrossSection, String(day), P.numH), 0.6).translate([sx * (Ox / 2 + P.tab / 2 + 1), 0, shelfT])
    return { card, num }
  }

  // ── fireplace (base) ───────────────────────────────────────────────────
  // Big enough to pass two Kinder eggs: arch ~74 wide, 64 high (≥ 50 mm free where the
  // shaft meets the ramp).
  const Bw = Ox + 44, Bf = Oy / 2 + 34, Bb = Oy / 2 + 8, Hb = 86
  const baseFoot = box(-Bw / 2, -Bf, 0, Bw / 2, Bb, Hb - 8)
    .add(box(-Bw / 2 - 4, -Bf - 4, Hb - 8, Bw / 2 + 4, Bb + 4, Hb - 2)) // mantel shelf
    .add(box(-Ox / 2 - 4, -Oy / 2 - 4, Hb - 2, Ox / 2 + 4, Oy / 2 + 4, Hb)) // collar under the chimney
    .add(box(-Bw / 2 - 2, -Bf - 10, 0, Bw / 2 + 2, Bb + 2, 3)) // hearth step
  // Fire chamber: arched opening at the front, ramp falling 20° towards it, open up into the shaft.
  const fw = Math.min(Ix - 6, 74), floor0 = 6, slope = Math.tan((20 * Math.PI) / 180)
  const archH = 64
  const rampZ = (y) => floor0 + (y + Bf + 3.3) * slope // (anchored 3.3 mm in front of the face so it never meets an edge)
  const chamberAt = (grow) => {
    const arch = Manifold.union([
      box(-fw / 2 - grow, -Bf - 12, floor0 - grow, fw / 2 + grow, Iy / 2 + grow, floor0 + archH - fw / 2),
      Manifold.cylinder(Iy / 2 + grow + Bf + 12, fw / 2 + grow, fw / 2 + grow, 64).rotate([-90, 0, 0]).translate([0, -Bf - 12, floor0 + archH - fw / 2]),
    ])
    const shaft = cbox(Ix + 2 * grow, Iy + 2 * grow, floor0, Hb + 1)
    // Ramp: everything below the sloping floor stays solid.
    const zr = (y) => rampZ(y) - grow
    const ramp = Manifold.extrude(CrossSection.ofPolygons([[[-Bf - 20, -5], [Bb + 20, -5], [Bb + 20, zr(Bb + 20)], [-Bf - 20, zr(-Bf - 20)]]]), Bw + 20).rotate([90, 0, 90]).translate([-Bw / 2 - 10, 0, 0])
    return arch.add(shaft).subtract(ramp)
  }
  const chamber = chamberAt(0), lining = chamberAt(1.4).subtract(chamber).intersect(baseFoot)
  let base = baseFoot.subtract(chamberAt(1.4)).add(spigot(Hb))
  const hearth = lining.subtract(box(-1e3, -1e3, Hb - 10, 1e3, 1e3, 1e3)) // black only down in the fireplace

  // ── chimney cap ────────────────────────────────────────────────────────
  const capH = 18
  const cap = cbox(Ox + 8, Oy + 8, 0, 6).add(cbox(Ox + 2, Oy + 2, 6, capH - 4)).add(cbox(Ox + 10, Oy + 10, capH - 4, capH))
    .subtract(cbox(Ix, Iy, -1, capH + 1)).subtract(socket())

  // ── Santa, head first in the chimney (lid) ─────────────────────────────
  // Soft and chunky like a toy: a grey tray closes the chimney; out of it comes Santa's round
  // bottom with a white hem, black belt and buckle, two bent legs in thick fur cuffs and big
  // round boots. Everything rounded, nothing small to break off.
  const sph = ([x, y, z], r) => Manifold.sphere(r, 48).translate([x, y, z])
  const capsule = (a, b, ra, rb) => Manifold.hull([sph(a, ra), sph(b, rb)])
  const norm = (v) => { const l = Math.hypot(...v); return v.map((x) => x / l) }
  // Builds along +z, then turns +z onto direction d and moves to p.
  const along = (m, d, p) => {
    const [dx, dy, dz] = norm(d)
    const a = (Math.asin(-dy) * 180) / Math.PI, b = (Math.atan2(dx, dz) * 180) / Math.PI
    return m.rotate([a, b, 0]).translate(p)
  }
  const roundDisc = (r, hgt, rr) => Manifold.revolve(CrossSection.square([r, hgt]).offset(-rr, 'Round').offset(rr, 'Round', 2, 16).intersect(CrossSection.square([r + 1, hgt + 2]).translate([0, -1])), 64)
  const lidT = 7
  const lid = cbox(Ix - 2 * fit - 0.4, Iy - 2 * fit - 0.4, -10, 0.5)
    .add(Manifold.hull([rbox(Ix + 6, Iy + 6, 0, 0.5, 6), rbox(Ox + 6, Oy + 6, lidT - 3, lidT, 8)]))
    .subtract(rbox(Ox - 1, Oy - 1, lidT - 1.5, lidT + 1, 6)) // a shallow tray
  const rx = Math.min(Ix * 0.42, 52), ry = Math.min(Iy * 0.5, 40), rz = 34, bz0 = lidT - 1.5
  const bum = Manifold.sphere(1, 96).scale([rx, ry, rz]).translate([0, 0, bz0 - 2])
  const above = box(-500, -500, bz0, 500, 500, 500)
  const shell = (k, z0, z1) => Manifold.sphere(1, 96).scale([rx * k, ry * k, rz * k]).translate([0, 0, bz0 - 2]).intersect(box(-500, -500, z0, 500, 500, z1)).subtract(bum)
  const hem = shell(1.07, bz0, bz0 + 5)
  const belt = shell(1.06, bz0 + 6, bz0 + 13)
  const byF = -ry * 1.06, bzC = bz0 + 9.5
  const buckle = Manifold.extrude(CrossSection.square([20, 14], true).offset(-3, 'Round').offset(3, 'Round', 2, 16).subtract(CrossSection.square([11, 6], true).offset(1, 'Round', 2, 12)), 3.2).rotate([90, 0, 0]).translate([0, byF + 1.6, bzC])
  // Legs: short, thick thigh and shin capsules bent at the knee; one up high, one tumbling
  // sideways — like a toy figure.
  const legSpec = [
    { A: [-rx * 0.42, 0, bz0 + rz * 0.6], K: [-rx * 0.95, -4, bz0 + rz + 14], F: [-rx * 1.05, -2, bz0 + rz + 40] },
    { A: [rx * 0.42, 0, bz0 + rz * 0.55], K: [rx * 1.0, 2, bz0 + rz + 8], F: [rx * 1.42, 0, bz0 + rz + 22] },
  ]
  const legs = [], cuffs = [], boots = []
  for (const { A, K, F } of legSpec) {
    legs.push(capsule(A, K, 21, 20), capsule(K, F, 20, 18))
    const d = [F[0] - K[0], F[1] - K[1], F[2] - K[2]], u = norm(d)
    // Puffy fur: a fat torus round the ankle.
    cuffs.push(along(Manifold.revolve(CrossSection.circle(7.5, 32).translate([17.5, 0]), 64), d, F))
    const bootAt = F.map((v, i) => v + u[i] * 6)
    const boot = roundDisc(21, 28, 9).add(Manifold.hull([sph([0, 0, 15], 15), sph([0, -15, 17], 13)]).intersect(box(-50, -50, 0, 50, 50, 28)))
    boots.push(along(boot, d, bootAt))
  }
  const black = Manifold.union([belt, ...boots]).subtract(buckle).subtract(lid)
  const white = Manifold.union([hem, ...cuffs]).subtract(Manifold.union(boots)).subtract(buckle).subtract(lid)
  const red = Manifold.union([bum.intersect(above), ...legs]).subtract(Manifold.union([hem, belt, ...cuffs, ...boots, buckle, lid]))
  const grey = lid.add(buckle)

  // ── assemble ───────────────────────────────────────────────────────────
  // Plain rings (no slot) above the last day give its treats extra height.
  const nPlain = Math.ceil(P.extraLast / h - 1e-9)
  const rings = Array.from({ length: days }, (_, i) => ({ day: i + 1, z: Hb + i * h }))
  const plains = Array.from({ length: nPlain }, (_, i) => ({ k: i, z: Hb + (days + i) * h }))
  const topZ = Hb + (days + nPlain) * h
  const shelves = rings.map(({ day, z }) => ({ day, ...shelf(day), z: z + slotZ + fit, side: sideOf(day) }))
  const capZ = topZ, santaZ = topZ + capH
  const GAP = 7 // exploded: rings apart by this much each
  const lastRoom = h - slotH + nPlain * h

  const toBed = (m) => { const b = m.boundingBox(); return m.translate([-b.min[0], -b.min[1], -b.min[2]]) }
  // Shelf plates: cards side by side, numbers up (a colour change for the top 0.6 mm).
  // As many cards per bed as fit, lying along or across (whichever holds more).
  const plates = []
  const cb = shelves[0].card.boundingBox(), cL = cb.max[0] - cb.min[0], cW = cb.max[1] - cb.min[1], usable = 262, gapP = 5
  const fitN = (a, b) => [Math.max(1, Math.floor((usable + gapP) / (a + gapP))), Math.max(1, Math.floor((usable + gapP) / (b + gapP)))]
  const [c1, r1] = fitN(cL, cW), [c2, r2] = fitN(cW, cL)
  const turn = c2 * r2 > c1 * r1, cols = turn ? c2 : c1, per = turn ? c2 * r2 : c1 * r1
  const cellX = (turn ? cW : cL) + gapP, cellY = (turn ? cL : cW) + gapP
  for (let i = 0; i < shelves.length; i += per) {
    const group = shelves.slice(i, i + per)
    // Same move for card and number, so they stay one object in the slicer.
    const lay = (s, j) => (m) => {
      const b = s.card.boundingBox()
      let x = m.translate([-b.min[0], -b.min[1], 0])
      if (turn) x = x.rotate([0, 0, 90]).translate([b.max[1] - b.min[1], 0, 0])
      return x.translate([(j % cols) * cellX, Math.floor(j / cols) * cellY, 0])
    }
    const cards = Manifold.union(group.map((s, j) => lay(s, j)(s.card)))
    const nums = Manifold.union(group.map((s, j) => lay(s, j)(s.num)))
    plates.push({ group, cards, nums })
  }

  const parts = []
  parts.push({
    id: 'ring', name: 'Kaminring', count: days, color: '#a8382d', filament: 'Rot',
    solid: Manifold.compose(rings.map((r) => ringFor(r.day).translate([0, 0, r.z]))),
    exploded: Manifold.compose(rings.map((r) => ringFor(r.day).translate([0, 0, r.z + r.day * GAP]))),
    print: toBed(ring), note: `${days}× drucken (ein Ring pro Tag) · aufrecht, ohne Stützen · jeder zweite Ring beim Stapeln um 180° gedreht`,
  })
  if (nPlain) parts.push({
    id: 'leerring', name: 'Leerring (ohne Schlitz)', count: nPlain, color: '#a8382d', filament: 'Rot',
    solid: Manifold.compose(plains.map((r) => plain.translate([0, 0, r.z]))),
    exploded: Manifold.compose(plains.map((r) => plain.translate([0, 0, r.z + (days + 1 + r.k) * GAP]))),
    print: toBed(plain), note: `${nPlain}× drucken · oben auf Tag ${days}: Platz für Größeres`,
  })
  const shelfExploded = (s) => [s.side * 50, 0, s.day * GAP]
  plates.forEach((pl, i) => {
    const days0 = pl.group[0].day, days1 = pl.group[pl.group.length - 1].day
    parts.push({
      id: `faecher-${days0}-${days1}`, name: `Fächer ${days0}–${days1}`, color: '#efece6', filament: 'Weiß', together: `plate${i}`,
      solid: Manifold.union(pl.group.map((s) => s.card.translate([0, 0, s.z]))),
      exploded: Manifold.union(pl.group.map((s) => s.card.translate([0, 0, s.z]).translate(shelfExploded(s)))),
      print: pl.cards, note: 'flach, ein Druckbett',
    })
    parts.push({
      id: `zahlen-${days0}-${days1}`, name: `Zahlen ${days0}–${days1}`, color: '#c0392b', filament: 'Rot', together: `plate${i}`,
      solid: Manifold.union(pl.group.map((s) => s.num.translate([0, 0, s.z]))),
      exploded: Manifold.union(pl.group.map((s) => s.num.translate([0, 0, s.z]).translate(shelfExploded(s)))),
      print: pl.nums, note: `zusammen mit „Fächer ${days0}–${days1}“ laden (ein Objekt, zwei Teile)`,
    })
  })
  const baseBed = (m) => m.translate([-base.boundingBox().min[0], -base.boundingBox().min[1], 0])
  parts.push({ id: 'kaminfuss', name: 'Kaminfuß', color: '#8d8f8a', filament: 'Grau', together: 'fuss', solid: base, exploded: base.translate([0, 0, -30]), print: baseBed(base), note: 'aufrecht drucken' })
  parts.push({ id: 'feuerraum', name: 'Feuerraum', color: '#232422', filament: 'Schwarz', together: 'fuss', solid: hearth, exploded: hearth.translate([0, 0, -30]), print: baseBed(hearth), note: 'zusammen mit „Kaminfuß“ laden (ein Objekt)' })
  parts.push({ id: 'kaminkopf', name: 'Kaminkopf', color: '#8d8f8a', filament: 'Grau', solid: cap.translate([0, 0, capZ]), exploded: cap.translate([0, 0, capZ + (days + nPlain + 2) * GAP]), print: toBed(cap), note: 'aufrecht' })
  const santaUp = (m) => m.translate([0, 0, santaZ])
  const santaEx = (m) => m.translate([0, 0, santaZ + (days + nPlain + 4) * GAP + 20])
  const santaAll = Manifold.compose([grey, red, white, black]).boundingBox()
  const santaBed = (m) => m.translate([-santaAll.min[0], -santaAll.min[1], -santaAll.min[2]])
  for (const [id, name, solid, color, filament] of [['santa-rot', 'Santa: Hose', red, '#c8302b', 'Rot'], ['santa-weiss', 'Santa: Fell', white, '#f4f2ee', 'Weiß'], ['santa-schwarz', 'Santa: Stiefel und Gürtel', black, '#1f1f1e', 'Schwarz'], ['santa-grau', 'Santa: Deckel und Schnalle', grey, '#9a9d97', 'Grau']]) {
    parts.push({ id, name, color, filament, together: 'santa', solid: santaUp(solid), exploded: santaEx(solid), print: santaBed(solid), note: 'alle vier Santa-Dateien als ein Objekt laden · Baumstützen unter den Stiefeln' })
  }

  const totalH = santaZ + santaAll.max[2]
  const issues = []
  if (P.room < 10) issues.push(`Nur ${(pitch - slotH).toFixed(1)} mm Platz pro Tag — für flache Süßigkeiten.`)
  if (Math.max(Ox + 10, Oy + 10) > 262) issues.push('Kaminkopf ist größer als das Druckbett.')
  if (Bw + 8 > 266 || Bb + Bf + 14 > 266) issues.push('Kaminfuß ist größer als das Druckbett.')
  const ghosts = []
  return {
    parts, ghosts, issues, params: P, size: [Bw + 8, Bb + Bf + 14, totalH],
    room: { inner: [Ix, Iy], perDay: pitch - slotH, last: lastRoom, archW: fw, chamberClear: floor0 + archH - rampZ(-Iy / 2) },
    info: [['Fach innen', `${Ix} × ${Iy} × ${(pitch - slotH).toFixed(1)} mm`], [`Tag ${days}`, `${Ix} × ${Iy} × ${lastRoom.toFixed(1)} mm`], ['Ringe', `${days} + ${nPlain} leer, je ${h} mm`], ['Gesamthöhe', `${totalH.toFixed(0)} mm mit Santa`]],
  }
}

